import React, { useRef, useEffect } from 'react';
import { Quote } from 'lucide-react';

export default function About() {
  const sectionRef = useRef(null);

  // Parallax spotlight — RAF-coalesced + coarse-pointer guarded so mobile
  // never pays the cost. `getBoundingClientRect()` is cached on resize/scroll
  // instead of being read per mousemove (was forcing a sync layout each
  // pointer pixel and visibly stuttering the section's reveals).
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (window.matchMedia('(pointer: coarse)').matches) return undefined;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
    const el = sectionRef.current;
    if (!el) return undefined;
    let rect = el.getBoundingClientRect();
    const cacheRect = () => { rect = el.getBoundingClientRect(); };
    let raf = 0;
    let lx = 0;
    let ly = 0;
    const onMove = (e) => {
      lx = e.clientX;
      ly = e.clientY;
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        el.style.setProperty('--mx', `${lx - rect.left}px`);
        el.style.setProperty('--my', `${ly - rect.top}px`);
      });
    };
    el.addEventListener('pointermove', onMove, { passive: true });
    window.addEventListener('scroll', cacheRect, { passive: true });
    window.addEventListener('resize', cacheRect, { passive: true });
    return () => {
      el.removeEventListener('pointermove', onMove);
      window.removeEventListener('scroll', cacheRect);
      window.removeEventListener('resize', cacheRect);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <section id="about" ref={sectionRef} className="spotlight relative py-16 sm:py-24 lg:py-32 bg-black overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-5 reveal-left">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-[2px] w-10 bg-red-600" />
            <span className="text-xs tracking-[0.4em] text-red-500 font-semibold">ABOUT US</span>
          </div>
          <h2 className="font-display text-5xl md:text-7xl leading-[0.9] tracking-tight">
            A FAMILY-FIRST<br />FITNESS <span className="text-gradient-red">COMMUNITY</span>
          </h2>
          <p className="mt-8 text-zinc-400 text-base leading-relaxed max-w-md">
            For 34 years, Fitness Pro has provided a place for young and old to experience their fitness in a comfortable, clean, safe and friendly environment. Whether you have never walked into a gym or are a seasoned athlete, your success is our goal.
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            {['CLEAN', 'SAFE', 'FRIENDLY', 'INCLUSIVE'].map((t) => (
              <span key={t} className="px-4 py-2 border border-red-600/30 rounded-full text-[11px] tracking-[0.2em] text-zinc-300 hover:border-red-500 hover:text-white transition">{t}</span>
            ))}
          </div>
        </div>

        <div className="lg:col-span-7 grid grid-cols-2 gap-4 reveal-right">
          <div className="space-y-4">
            <div className="relative h-64 rounded-2xl overflow-hidden group">
              <img src="https://images.unsplash.com/photo-1605296867304-46d5465a13f1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwxfHxneW0lMjB3b3Jrb3V0fGVufDB8fHx8MTc3ODE3ODQyN3ww&ixlib=rb-4.1.0&q=85" alt="" loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
              <div className="absolute bottom-4 left-4">
                <div className="font-display text-3xl text-white leading-none">17,000<span className="text-red-500">.</span></div>
                <div className="mt-2 text-[10px] tracking-[0.3em] text-zinc-400">SQ FT FACILITY</div>
              </div>
            </div>
            <div className="relative h-48 rounded-2xl overflow-hidden plate-3d border border-red-600/20 p-6 flex flex-col justify-between">
              <Quote className="w-8 h-8 text-red-500" />
              <div>
                <p className="text-sm italic text-zinc-300 leading-relaxed">“We do not stop exercising because we grow old — we grow old because we stop exercising.”</p>
                <p className="mt-3 text-[10px] tracking-[0.3em] text-red-500">— DR. KENNETH COOPER</p>
              </div>
            </div>
          </div>
          <div className="space-y-4 mt-12">
            {/* === ON-SITE KID CARE — image + high-converting verbiage === */}
            <a
              href="#kid-care"
              data-testid="about-kidcare-card"
              className="relative block h-48 rounded-2xl overflow-hidden group plate-3d border border-red-600/30 hover:border-red-500 transition"
            >
              <img
                src={`${process.env.REACT_APP_BACKEND_URL}/api/static/media/kid-care-gym.png`}
                alt="On-site Kid Care at FitnessPro Galt"
                loading="lazy"
                decoding="async"
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000"
              />
              {/* Lighter gradient — keeps text legible while letting the AI image breathe */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/30 to-black/10" />
              <div className="absolute top-3 left-3 inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-black/85 backdrop-blur border border-red-500/55 shadow-[0_4px_18px_rgba(239,43,43,0.4)]">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-red-500" />
                </span>
                <span className="text-[9px] tracking-[0.35em] text-white font-bold">ON-SITE KID CARE</span>
              </div>
              <div className="absolute bottom-3 left-3 right-3">
                <div className="font-display text-xl text-white leading-tight">
                  Workouts Without <span className="text-red-500">Compromise.</span>
                </div>
                <div className="mt-1 text-[10px] tracking-[0.3em] text-zinc-300 font-semibold">
                  AFFORDABLE ADD-ON · TRUSTED ATTENDANTS
                </div>
              </div>
            </a>

            <div className="relative h-64 rounded-2xl overflow-hidden group">
              <img src="https://images.unsplash.com/photo-1517836357463-d25dfeac3438?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHw0fHxmaXRuZXNzJTIwdHJhaW5pbmd8ZW58MHx8fHwxNzc4MTc4NDI3fDA&ixlib=rb-4.1.0&q=85" alt="" loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
              <div className="absolute bottom-4 left-4">
                <div className="font-display text-2xl text-white">24/7</div>
                <div className="text-[10px] tracking-[0.3em] text-zinc-400 mt-1">ALWAYS OPEN</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
